import React, { Suspense, lazy } from 'react';
import * as Sentry from '@sentry/react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import ErrorBoundary from './components/ErrorBoundary';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import VisualSkin from './components/VisualSkin';
import PostHogProvider from './components/PostHogProvider';
import { OrganizationSchema } from './components/StructuredData';

// Lazy load pages and modals for code splitting - reduces initial bundle size
const ProfileCompletionModal = lazy(
  () => import('./modals/ProfileCompletionModal')
);
const Home = lazy(() => import('./pages/Home'));
const Market = lazy(() => import('./pages/Market'));
const FindGames = lazy(() => import('./pages/FindGames'));
const Discover = lazy(() => import('./pages/Discover'));
const UploadGame = lazy(() => import('./pages/UploadGame'));
const BorrowRequest = lazy(() => import('./pages/BorrowRequest'));
const Favorites = lazy(() => import('./pages/Favorites'));
const Messages = lazy(() => import('./pages/Messages'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./pages/PaymentCancel'));
const PaymentsHistory = lazy(() => import('./pages/PaymentsHistory'));
const Profile = lazy(() => import('./pages/Profile'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminPayments = lazy(() => import('./pages/AdminPayments'));
const MyGames = lazy(() => import('./pages/MyGames'));
const MyLibraries = lazy(() => import('./pages/MyLibraries'));
const LibraryDetail = lazy(() => import('./pages/LibraryDetail'));
const Friends = lazy(() => import('./pages/Friends'));
const TermsOfServices = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const InspectionWindow = lazy(() => import('./components/InspectionWindow'));
const NearbyLibraries = lazy(() => import('./pages/NearbyLibraries'));
const BorrowSettings = lazy(() => import('./pages/BorrowSettings'));
const NotFound = lazy(() => import('./pages/NotFound'));
const EditGame = lazy(() => import('./pages/EditGame'));

import { Analytics } from '@vercel/analytics/react';

// Loading fallback component for Suspense - FIR-155
const PageLoader = () => (
  <div className="page-loader" role="status" aria-busy="true">
    <div className="page-loader-spinner" aria-hidden="true" />
    <span className="sr-only">Loading page content...</span>
  </div>
);
import './styles/App.css';

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <div key={location.pathname} className="page-transition">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <main className="main-content" role="main" id="main-content">
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes location={location}>
              <Route path="/" element={<Home />} />
              <Route path="/market" element={<Market />} />
              <Route path="/discover" element={<Discover />} />
              <Route path="/find-games" element={<FindGames />} />
              <Route path="/upload" element={<UploadGame />} />
              <Route path="/borrow" element={<BorrowRequest />} />
              <Route path="/favorites" element={<Favorites />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/messages/*" element={<Messages />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/payment-cancel" element={<PaymentCancel />} />
              <Route path="/payments" element={<PaymentsHistory />} />
              <Route path="/payments-history" element={<PaymentsHistory />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/borrow-settings" element={<BorrowSettings />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/register/*" element={<Register />} />
              <Route path="/login/*" element={<Login />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/payments" element={<AdminPayments />} />
              <Route path="/my-games" element={<MyGames />} />
              <Route path="/edit-game/:gameId" element={<EditGame />} />
              <Route path="/libraries" element={<MyLibraries />} />
              <Route path="/libraries/nearby" element={<NearbyLibraries />} />
              <Route path="/libraries/:libraryId" element={<LibraryDetail />} />
              <Route path="/friends" element={<Friends />} />
              <Route path="/terms-of-service" element={<TermsOfServices />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/inspections" element={<InspectionWindow />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
}

// Accessibility: Skip link for keyboard navigation
const SkipLink = () => (
  <a
    href="#main-content"
    className="skip-link"
    style={{
      position: 'absolute',
      top: '-40px',
      left: 0,
      background: 'var(--primary-color)',
      color: '#fff',
      padding: '8px 16px',
      zIndex: 10000,
      textDecoration: 'none',
      borderRadius: '0 0 4px 0',
      transition: 'top 0.2s ease',
    }}
    onFocus={(e) => (e.target.style.top = '0')}
    onBlur={(e) => (e.target.style.top = '-40px')}
  >
    Skip to main content
  </a>
);

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={<p>An error has occurred. Please refresh the page.</p>}
    >
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <PostHogProvider>
          <OrganizationSchema />
          <SkipLink />
          <VisualSkin />
          <Toaster position="top-right" />
          <div className="App relative z-10">
            <ErrorBoundary>
              <Navbar />
            </ErrorBoundary>
            <ErrorBoundary>
              <Suspense fallback={null}>
                <ProfileCompletionModal />
              </Suspense>
            </ErrorBoundary>
            <AnimatedRoutes />
            <ErrorBoundary>
              <Footer />
            </ErrorBoundary>
          </div>
        </PostHogProvider>
      </Router>
      <Analytics />
    </Sentry.ErrorBoundary>
  );
}

export default App;
